import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Search } from 'lucide-react';
import type { GeocodingResult } from '@aimmo/contracts';
import { Input, Spinner } from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';

/** Long enough that a street name is half typed before the first request. */
const MIN_QUERY = 4;
const DEBOUNCE_MS = 400;

export interface AddressSearchFieldProps {
  disabled?: boolean;
}

/**
 * Types an address, fills the block (U3).
 *
 * It writes six sibling fields through the form context rather than owning any
 * of them: street, number, CAP, comune, and the two coordinates. Everything it
 * writes stays editable — the geocoder proposes, the person entering the
 * property decides.
 *
 * The comune is only set when the API could resolve it to an ISTAT code it is
 * sure of. A wrong comune silently breaks zone and radius search, so an empty
 * one — a question the user can answer — is the better failure.
 */
export function AddressSearchField({ disabled = false }: AddressSearchFieldProps) {
  const form = useFormContext();
  const inputId = React.useId();
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<GeocodingResult[]>([]);
  const [pending, setPending] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const text = query.trim();
    if (text.length < MIN_QUERY) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      void (async () => {
        setPending(true);
        setFailed(false);
        try {
          const found = assertOk(
            await api.geocoding.search({
              query: { q: text, limit: 5 },
              fetchOptions: { signal: controller.signal },
            }),
          ).body;
          setResults(found);
          setOpen(true);
        } catch {
          // Provider down, over quota, or offline: the address block below is
          // still typeable, which is what it was before this field existed.
          setResults([]);
          setFailed(true);
        } finally {
          setPending(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query]);

  function choose(result: GeocodingResult): void {
    const set = (name: string, value: unknown) =>
      form.setValue(name, value, { shouldDirty: true, shouldValidate: true });

    set('addressStreet', result.street ?? '');
    set('addressNumber', result.number ?? '');
    set('addressPostalCode', result.postalCode ?? '');
    if (result.comuneId) set('comuneId', result.comuneId);
    set('lat', Number(result.lat.toFixed(6)));
    set('lng', Number(result.lng.toFixed(6)));

    setQuery('');
    setResults([]);
    setOpen(false);
  }

  return (
    <div className="grid gap-1" data-slot="address-search">
      <label htmlFor={inputId} className="text-label uppercase tracking-[0.08em] text-faint">
        Cerca indirizzo
      </label>
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted" />
        <Input
          id={inputId}
          className="pl-8"
          placeholder="Via Dante 1, Milano"
          autoComplete="off"
          disabled={disabled}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setOpen(results.length > 0)}
        />
        {pending && (
          <Spinner className="absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted" />
        )}
      </div>

      {failed && (
        <p className="text-secondary text-muted">
          Ricerca non disponibile: compila i campi qui sotto o usa la mappa.
        </p>
      )}

      {open && results.length > 0 && (
        <ul className="grid gap-0.5 rounded-control border border-border bg-surface p-1">
          {results.map((result) => (
            <li key={`${result.lat},${result.lng},${result.label}`}>
              <button
                type="button"
                className="w-full rounded-control px-2 py-1.5 text-left text-body text-ink-2 hover:bg-surface-sunken hover:text-ink"
                onClick={() => choose(result)}
              >
                {result.label}
                {result.comuneId === null && result.comuneName && (
                  <span className="block text-secondary text-muted">
                    Comune da confermare a mano
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
